/**
 * care_plans stores prose, not structured goals — `current_priorities` and
 * `whats_not_working` are free text. The care-plan generator on
 * getmapacare.com writes them as bullet lines, so parse those back into cards
 * and degrade gracefully to a single block of prose for hand-written plans.
 *
 * Expected shapes (both tolerated):
 *   • [high] Improve medication consistency — She skips doses…
 *   • Improve medication consistency — She skips doses…
 */

const PRIORITIES = ['high', 'medium', 'ongoing']
const SEVERITIES = ['high', 'medium']

function splitBullets(text) {
  if (!text) return []
  return String(text)
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.replace(/^[•\-*]\s*/, ''))
    .filter(Boolean)
}

function splitTitleBody(line) {
  // em dash first (what we write), then hyphen, then colon.
  const m = line.match(/^(.+?)\s+[—–]\s+(.+)$/) || line.match(/^(.+?)\s+-\s+(.+)$/) || line.match(/^(.+?):\s+(.+)$/)
  if (m) return { title: m[1].trim(), body: m[2].trim() }
  return { title: line.trim(), body: '' }
}

export function parseGoals(currentPriorities) {
  const lines = splitBullets(currentPriorities)
  if (lines.length === 0) return []
  return lines.map((raw, i) => {
    let priority = null
    let line = raw
    const tag = line.match(/^\[(\w+)\]\s*/)
    if (tag && PRIORITIES.includes(tag[1].toLowerCase())) {
      priority = tag[1].toLowerCase()
      line = line.slice(tag[0].length)
    }
    const { title, body } = splitTitleBody(line)
    return {
      id: `goal-${i}`,
      priority: priority || 'ongoing',
      title,
      description: body,
    }
  })
}

export function parseRiskFlags(whatsNotWorking) {
  const lines = splitBullets(whatsNotWorking)
  if (lines.length === 0) return []
  return lines.map((raw, i) => {
    let severity = 'medium'
    let line = raw
    const tag = line.match(/^\[(\w+)\]\s*/)
    if (tag && SEVERITIES.includes(tag[1].toLowerCase())) {
      severity = tag[1].toLowerCase()
      line = line.slice(tag[0].length)
    }
    const { title, body } = splitTitleBody(line)
    return { id: `risk-${i}`, severity, flag: title, reason: body }
  })
}

/**
 * What gets measured for a goal — drives the tracking chips on each card.
 * Keyword-matched against the goal text so it works for any plan.
 */
const TRACKING_RULES = [
  { match: ['medication', 'dose', 'pill'], chips: ['Medication adherence', 'Missed doses'] },
  { match: ['fall', 'safety', 'balance', 'mobility', 'walk'], chips: ['Falls', 'Movement', 'Mobility test'] },
  { match: ['memory', 'cognit', 'confus'], chips: ['Memory recall', 'Confusion episodes'] },
  { match: ['mood', 'depress', 'lonely', 'isolat', 'social'], chips: ['Mood', 'Social contact'] },
  { match: ['weight', 'appetite', 'meal', 'nutrition', 'eat'], chips: ['Appetite', 'Weight'] },
  { match: ['blood pressure', 'hypertension', 'bp', 'heart'], chips: ['Blood pressure'] },
  { match: ['sleep'], chips: ['Sleep'] },
  { match: ['home', 'independen'], chips: ['Personal care', 'Independence'] },
]

export function trackingChips(goal) {
  const hay = `${goal.title} ${goal.description}`.toLowerCase()
  const chips = []
  for (const rule of TRACKING_RULES) {
    if (rule.match.some((m) => hay.includes(m))) {
      for (const c of rule.chips) if (!chips.includes(c)) chips.push(c)
    }
  }
  return chips.slice(0, 3)
}
